/**
 * HTTP fetch for SPA navigation.
 *
 * Builds the X-SDX-* header stack the server consumes to decide
 * whether to return a partial JSON envelope or a full HTML body,
 * then dispatches the request and normalizes the response into
 * the FetchResult discriminated union.
 *
 * Header contract:
 * - `X-SDX-Navigate: 1` - unconditional marker for every SPA fetch.
 * - `X-SDX-Scope: content` - sent only for content-scope fetches;
 *   content-scope also attaches `X-SDX-Menu-Hash` and
 *   `X-SDX-Route` so the server can auto-upgrade to full scope if
 *   the route changed since the hash was captured.
 * - `X-SDX-Stale: k1,k2,...` - set when the controller knows which
 *   DataProvider prop keys are stale based on URL param diffs.
 * - `X-SDX-Tags: tag:checksum,...` - content-scope only; the
 *   server skips tags whose client-side checksum is still fresh.
 * - `X-SDX-Region-Tags: region:checksum,...` - sent on every
 *   fetch so the server can strip regions whose cache tags
 *   have not changed.
 * - `X-SDX-Ajax-Libraries: <compressed>` - forwards the current
 *   document's loaded-library manifest. Without this, every SPA
 *   navigation crossing a page boundary with a different
 *   aggregated bundle hash would re-execute `core/misc/drupal.js`,
 *   which unconditionally resets `window.Drupal = { behaviors: {} }`
 *   and would wipe every behaviour registered on the initial page
 *   load (including `Drupal.sdxNavigation` itself). Headers survive
 *   same-origin redirects, so this value reaches the target of the
 *   303 Drupal form handlers emit after a successful POST - where
 *   the equivalent query-parameter would have been dropped.
 *   `sdx_router`'s `SdxAjaxLibrariesHeader` stack middleware lifts
 *   the header into `$request->query['ajax_page_state']` so core's
 *   `AjaxPageState` middleware processes it uniformly.
 */

import { SDX_HEADERS } from '@sdx/core/transport';
import type { FetchResult, NavScope, PartialResponseEnvelope } from './types';
import type { TagStore } from './tag-store';
import type { RegionChecksums } from './region-checksums';

export interface FetchPageContext {
  readonly tagStore: TagStore;
  readonly regionChecksums: RegionChecksums;
  /** Client-side tracked menu hash from the most recent response. */
  menuHash: string;
  /** Client-side tracked route name from the most recent response. */
  routeName: string;
}

export function fetchPage(
  url: string,
  signal: AbortSignal,
  scope: NavScope,
  staleKeys: string[],
  ctx: FetchPageContext,
): Promise<FetchResult> {
  const headers: Record<string, string> = { [SDX_HEADERS.NAVIGATE]: '1' };

  if (scope === 'content') {
    headers[SDX_HEADERS.SCOPE] = 'content';
    if (ctx.menuHash) headers[SDX_HEADERS.MENU_HASH] = ctx.menuHash;
    if (ctx.routeName) headers[SDX_HEADERS.ROUTE] = ctx.routeName;

    // Tag checksums only make sense for same-route fetches: for a
    // different route, props depend on route parameters rather
    // than just cache tags, and sending stale checksums across a
    // route boundary causes the server to omit props it thinks
    // are unchanged but actually aren't.
    const tagHeader = ctx.tagStore.getTagHeader();
    if (tagHeader) {
      headers[SDX_HEADERS.TAGS] = tagHeader;
    }
  }

  if (staleKeys.length > 0) {
    headers[SDX_HEADERS.STALE] = staleKeys.join(',');
  }

  if (!ctx.regionChecksums.isEmpty()) {
    headers[SDX_HEADERS.REGION_TAGS] = ctx.regionChecksums.getHeader();
  }

  const libraries = readLoadedLibraries();
  if (libraries !== null) {
    headers[SDX_HEADERS.AJAX_LIBRARIES] = libraries;
  }

  return fetch(url, {
    credentials: 'same-origin',
    headers,
    signal,
  }).then((response) => normalizeResponse(response, url));
}

/**
 * Normalizes a Response into the SDX FetchResult discriminated union.
 *
 * Extracted from `fetchPage` so the navigation controller can adopt
 * an already-dispatched Response (notably the response to a form
 * POST handled by `installFormListener`) instead of re-fetching the
 * same URL - which would discard the POST's body, consume the
 * Drupal messenger flash on the initial render, and leave no
 * feedback for the user on the inevitable follow-up GET.
 *
 * `requestUrl` is the original request URL. The Response object's
 * own `.url` reflects the URL after any redirects the fetch
 * followed; the former is what we use to pull a `destination`
 * query parameter for the batch-modal return path.
 */
export function normalizeResponse(
  response: Response,
  requestUrl: string,
): Promise<FetchResult> {
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  // Batch redirect: the server followed a redirect to /batch.
  // Return a typed result so the controller can launch the batch
  // modal in-place instead of rendering a page.
  if (isBatchUrl(response.url)) {
    let returnUrl = location.href;
    try {
      const dest = new URL(requestUrl, location.origin).searchParams.get('destination');
      if (dest) returnUrl = new URL(dest, location.origin).href;
    }
    catch { /* keep current page */ }
    return Promise.resolve({
      type: 'batch' as const,
      batchUrl: response.url,
      returnUrl,
    });
  }

  // When the fetch followed a redirect (an alias 301ing to its
  // canonical path, a language-home alias, a trailing-slash fix),
  // surface the URL the content actually belongs to so the
  // controller can put IT in the address bar and nav state - the
  // same URL a non-SPA click would have landed on.
  const finalUrl = response.redirected ? response.url : undefined;

  const isPartial = response.headers.get(SDX_HEADERS.PARTIAL) === '1'
    || (response.headers.get('Content-Type') ?? '').includes('application/json');
  if (isPartial) {
    return response.json().then((data: PartialResponseEnvelope) => ({
      type: 'partial' as const,
      data,
      finalUrl,
    }));
  }
  return response.text().then((html) => ({
    type: 'html' as const,
    data: html,
    finalUrl,
  }));
}

function isBatchUrl(urlString: string): boolean {
  try {
    return new URL(urlString, location.origin).pathname === '/batch';
  } catch {
    return false;
  }
}

/**
 * Reads the current document's compressed loaded-library manifest.
 *
 * The value is exactly what Drupal core originally injected into
 * `drupalSettings.ajaxPageState.libraries` for this page: a
 * base64url-encoded, gzip-compressed list of library names. The
 * server's `AjaxPageState` middleware round-trips it through its
 * own decompression path, so the client hands the value back
 * verbatim - no round-trip of its own.
 *
 * Returns `null` when the value is missing or empty so the caller
 * can omit the header entirely rather than sending a falsy stub.
 */
export function readLoadedLibraries(): string | null {
  const ajaxPageState = drupalSettings.ajaxPageState as { libraries?: string } | undefined;
  const libraries = ajaxPageState?.libraries;
  return typeof libraries === 'string' && libraries !== '' ? libraries : null;
}
